// HyperliquidMetricsBar — Header-Streifen oben in der Hyperliquid-Page.
// 12-Coin-Selektor (links) + 6 Live-Metrics (rechts).
//
// Coins werden von der Page kontrolliert (selected + onSelect). Metrics werden
// idealerweise aus useHyperliquidGlobal().liveMetrics[coin] gefüttert; wenn
// dort nichts vorliegt, zeigt die Bar dezente „—"-Platzhalter.

import type { HLAssetCtx } from '@/types/hyperliquid';

export const HYPERLIQUID_COINS = [
  'BTC',
  'ETH',
  'SOL',
  'BNB',
  'XRP',
  'SUI',
  'TAO',
  'DOGE',
  'AVAX',
  'LINK',
  'ARB',
  'OP',
] as const;
export type HyperliquidCoin = (typeof HYPERLIQUID_COINS)[number];

interface HyperliquidMetricsBarProps {
  selected: HyperliquidCoin;
  onSelect: (coin: HyperliquidCoin) => void;
  /** AssetCtx für den selected coin. null = keine Live-Daten verfügbar. */
  metrics: HLAssetCtx | null;
  /** Live-WS connected? Steuert das LIVE/REST-Badge neben Mark. */
  isConnected: boolean;
  /** Optional Delta zum OI (Δ-Pfeil neben OI-Wert). */
  oiDelta?: number;
  /** Optional Vol 24h in USD. */
  volume24hUsd?: number;
}

export function HyperliquidMetricsBar({
  selected,
  onSelect,
  metrics,
  isConnected,
  oiDelta,
  volume24hUsd,
}: HyperliquidMetricsBarProps) {
  return (
    <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-white/5 px-4 py-3">
      {/* 12-Coin-Selektor */}
      <div className="flex flex-wrap gap-1">
        {HYPERLIQUID_COINS.map((coin) => {
          const active = coin === selected;
          return (
            <button
              key={coin}
              type="button"
              onClick={() => onSelect(coin)}
              className={
                active
                  ? 'rounded-md border border-amber-500/30 bg-amber-500/20 px-2.5 py-1 text-[12px] font-bold text-amber-300'
                  : 'rounded-md border border-neutral-700/30 bg-neutral-800/60 px-2.5 py-1 text-[12px] font-bold text-neutral-400 hover:text-white'
              }
            >
              {coin}
            </button>
          );
        })}
      </div>

      {/* 6 Live-Metrics */}
      <div className="flex flex-wrap items-baseline gap-4">
        <Metric label="Mark">
          <div className="flex items-center gap-1">
            <span className="text-sm font-bold tabular">{formatPrice(metrics?.markPx)}</span>
            <span
              className={`rounded px-1 text-[10px] font-bold ${
                isConnected
                  ? 'bg-emerald-500/15 text-emerald-400'
                  : 'bg-neutral-700/40 text-neutral-400'
              }`}
            >
              {isConnected ? 'LIVE' : 'REST'}
            </span>
          </div>
        </Metric>
        <Metric label="Funding">
          <span
            className={`text-sm font-bold tabular ${
              (metrics?.funding ?? 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'
            }`}
          >
            {formatPercent(metrics?.funding, 4)}
          </span>
        </Metric>
        <Metric label="Premium">
          <span
            className={`text-sm font-bold tabular ${
              (metrics?.premium ?? 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'
            }`}
          >
            {formatPercent(metrics?.premium, 4)}
          </span>
        </Metric>
        <Metric label="OI">
          <div className="flex items-center gap-1">
            <span className="text-sm font-bold tabular text-amber-400">
              {formatBigUsd(metrics?.openInterest)}
            </span>
            {typeof oiDelta === 'number' && oiDelta !== 0 ? (
              <span
                className={`text-[11px] font-bold ${
                  oiDelta > 0 ? 'text-emerald-400' : 'text-rose-400'
                }`}
              >
                {oiDelta > 0 ? '▲' : '▼'}
                {formatBigUsd(Math.abs(oiDelta))}
              </span>
            ) : null}
          </div>
        </Metric>
        <Metric label="Vol 24h">
          <span className="text-sm font-bold tabular text-neutral-300">
            {formatBigUsd(volume24hUsd)}
          </span>
        </Metric>
        <Metric label="Oracle">
          <span className="text-sm tabular text-neutral-400">{formatPrice(metrics?.oraclePx)}</span>
        </Metric>
      </div>
    </div>
  );
}

function Metric({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-neutral-500">{label}</div>
      {children}
    </div>
  );
}

function formatPrice(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return '—';
  if (value >= 1000) return `$${value.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  return `$${value.toFixed(2)}`;
}

function formatPercent(value: number | undefined, decimals = 4): string {
  if (value === undefined || !Number.isFinite(value)) return '—';
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${Math.abs(value * 100).toFixed(decimals)}%`;
}

function formatBigUsd(value: number | undefined): string {
  if (value === undefined || !Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}
